import { spineConfig, type SpineAssets } from "../data/spineConfig";

export type SpineBone = {
  worldX: number;
  worldY: number;
};

export type SpineSprite = {
  x: number;
  y: number;
  scale: { x: number; y: number; set: (n: number) => void };
  getBounds?: () => { x: number; y: number; width: number; height: number };
  state: {
    setAnimation?: (track: number, name: string, loop: boolean) => void;
    setAnimationByName?: (track: number, name: string, loop: boolean) => void;
    addListener: (listener: Record<string, unknown>) => void;
  };
  skeleton?: {
    flipX?: boolean;
    findBone?: (name: string) => SpineBone | null;
    updateWorldTransform?: () => void;
  };
  skeletonData?: { animations?: { name: string }[] };
  spineData?: { animations?: { name: string }[] };
  interactive: boolean;
  buttonMode: boolean;
  visible?: boolean;
  on: (event: string, fn: () => void) => void;
};

export type PixiDisplay = {
  x: number;
  y: number;
  visible: boolean;
  alpha?: number;
  rotation?: number;
  scale: { x: number; y: number; set: (x: number, y?: number) => void };
  anchor?: { set: (x: number, y?: number) => void };
  blendMode?: number;
  texture?: unknown;
};

export type PixiApp = {
  view: HTMLCanvasElement;
  ticker: { add: (fn: (delta: number) => void) => void };
  stage: { addChild: (child: unknown) => void; removeChildren: () => void };
  renderer: { resize: (w: number, h: number) => void };
  destroy: (removeView?: boolean, stage?: boolean) => void;
};

type Loader = {
  add: (name: string, url: string, options?: Record<string, unknown>) => Loader;
  load: (
    cb: (loader: unknown, resources: Record<string, { spineData?: unknown }>) => void,
  ) => void;
};

type SpineRuntimeNs = {
  Atlas: new (
    text: string,
    loader: (line: string, cb: (texture: unknown) => void) => void,
    done?: (atlas: unknown) => void,
  ) => unknown;
  AtlasAttachmentParser: new (atlas: unknown) => unknown;
  SkeletonJsonParser: new (parser: unknown) => { readSkeletonData: (json: unknown, name?: string) => unknown };
};

export type PixiRuntime = {
  Application: new (options: Record<string, unknown>) => PixiApp;
  BaseTexture: new (source: HTMLImageElement) => unknown;
  Texture: new (base: unknown, frame?: unknown) => unknown;
  Rectangle: new (x: number, y: number, w: number, h: number) => unknown;
  Sprite: new (texture: unknown) => PixiDisplay;
  Graphics: new () => PixiDisplay & {
    beginFill: (color: number, alpha?: number) => void;
    drawCircle: (x: number, y: number, r: number) => void;
    endFill: () => void;
  };
  BLEND_MODES?: { ADD: number };
  loaders?: { Loader: new () => Loader };
  Loader?: new () => Loader;
  spine: {
    Spine: new (spineData: unknown) => SpineSprite;
    SpineRuntime?: SpineRuntimeNs;
  };
};

declare global {
  interface Window {
    PIXI?: PixiRuntime;
    SkeletonBinary?: new () => {
      data: Uint8Array;
      json: { bones?: unknown; animations?: Record<string, unknown> };
      initJson: () => void;
    };
  }
}

const scriptLoads = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const cached = scriptLoads.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[data-src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === "1") {
        resolve();
        return;
      }
      existing.addEventListener(
        "load",
        () => {
          existing.dataset.loaded = "1";
          resolve();
        },
        { once: true },
      );
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
      // Already executed (HMR / cached) — load will not fire again.
      window.setTimeout(() => {
        if (existing.dataset.loaded === "1") return;
        existing.dataset.loaded = "1";
        resolve();
      }, 0);
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.src = src;
    script.onload = () => {
      script.dataset.loaded = "1";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
  scriptLoads.set(src, promise);
  return promise;
}

function naganekoGlobals() {
  const g = window as Window & {
    enable_clear_fail_step?: boolean;
    skip_unpma?: boolean;
    goto_c2g?: () => void;
  };
  if (typeof g.enable_clear_fail_step === "undefined") g.enable_clear_fail_step = false;
  if (typeof g.skip_unpma === "undefined") g.skip_unpma = true;
  if (typeof g.goto_c2g !== "function") g.goto_c2g = () => {};
}

export async function loadSpineRuntime(): Promise<PixiRuntime> {
  naganekoGlobals();
  const existing = window.PIXI;
  if (existing?.spine?.SpineRuntime && window.SkeletonBinary) {
    return existing;
  }
  await loadScript(spineConfig.vendor.pixi);
  naganekoGlobals();
  await loadScript(spineConfig.vendor.pixiSpine);
  await loadScript(spineConfig.vendor.skelBinary);
  for (let i = 0; i < 40; i++) {
    const pixi = window.PIXI;
    if (pixi?.spine && window.SkeletonBinary) {
      naganekoGlobals();
      return pixi;
    }
    await new Promise((r) => window.setTimeout(r, 50));
  }
  if (!window.PIXI?.spine) {
    throw new Error("Pixi Spine 2.1 runtime did not attach to window.PIXI");
  }
  throw new Error("naganeko skb.js did not attach SkeletonBinary");
}

export function boneStagePos(sprite: SpineSprite, name: string): { x: number; y: number } | null {
  sprite.skeleton?.updateWorldTransform?.();
  const bone = sprite.skeleton?.findBone?.(name);
  if (!bone) return null;
  return {
    x: sprite.x + bone.worldX * sprite.scale.x,
    y: sprite.y + bone.worldY * sprite.scale.y,
  };
}

export function playClip(sprite: SpineSprite, name: string, loop: boolean) {
  const state = sprite.state;
  if (typeof state.setAnimationByName === "function") {
    state.setAnimationByName(0, name, loop);
    return;
  }
  state.setAnimation?.(0, name, loop);
}

export async function loadPngImage(src: string): Promise<HTMLImageElement> {
  const res = await fetch(src);
  if (!res.ok) throw new Error(`png missing: ${src}`);
  return blobToImage(await res.blob());
}

export function sliceSpriteSheet(
  pixi: PixiRuntime,
  img: HTMLImageElement,
  cols: number,
  rows: number,
): unknown[] {
  const base = new pixi.BaseTexture(img);
  const fw = img.width / cols;
  const fh = img.height / rows;
  const frames: unknown[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      frames.push(new pixi.Texture(base, new pixi.Rectangle(c * fw, r * fh, fw, fh)));
    }
  }
  return frames;
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("PNG failed to decode"));
    };
    img.src = url;
  });
}

/** Load a GFL Spine 2.1.27 binary pack the way naganeko spine-gif-extract does. */
export async function loadGflSpineData(pixi: PixiRuntime, pack: SpineAssets): Promise<{
  spineData: unknown;
  animations: string[];
}> {
  const Binary = window.SkeletonBinary;
  const runtime = pixi.spine.SpineRuntime;
  if (!Binary || !runtime) throw new Error("naganeko Spine 2.1 runtime incomplete");

  const [skelRes, atlasRes, pngRes] = await Promise.all([
    fetch(pack.skel),
    fetch(pack.atlas),
    fetch(pack.png),
  ]);
  if (!skelRes.ok) throw new Error(`skel missing: ${pack.skel}`);
  if (!atlasRes.ok) throw new Error(`atlas missing: ${pack.atlas}`);
  if (!pngRes.ok) throw new Error(`png missing: ${pack.png}`);

  const skelBuf = await skelRes.arrayBuffer();
  const atlasText = await atlasRes.text();
  const png = await blobToImage(await pngRes.blob());
  const page = new pixi.BaseTexture(png);

  const bin = new Binary();
  bin.data = new Uint8Array(skelBuf);
  bin.initJson();
  const json = bin.json;
  if (!json?.bones) throw new Error("SkeletonBinary produced no bones");

  const atlas = await new Promise<unknown>((resolve, reject) => {
    let settled = false;
    const finish = (value: unknown) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    try {
      const created = new runtime.Atlas(
        atlasText,
        (_line, cb) => cb(page),
        (parsed) => finish(parsed ?? created),
      );
      window.setTimeout(() => {
        if (!settled) finish(created);
      }, 0);
    } catch (err) {
      reject(err);
    }
  });

  const attachment = new runtime.AtlasAttachmentParser(atlas);
  const parser = new runtime.SkeletonJsonParser(attachment);
  const spineData = parser.readSkeletonData(json, pack.id);
  const anims = (spineData as { animations?: { name: string }[] }).animations ?? [];
  const animations = anims.map((a) => a.name).filter(Boolean);
  return { spineData, animations };
}
